// "Ne önerilmişti" popover'ı — Geçmiş Rapor Aktarımı (Task 15, tasarım review 13A).
//
// Reddedilen bir köken girdisinde ("Aktarılmadı · kaynak doğrulanamadı") kullanıcı
// "öneriyi gör" der; burada AI'nın önerdiği değer (`formatFormValue` ile okunur
// biçimde), raporda dayandığı alıntı ve red sebebi görünür. "Bu değeri kullan"
// değeri NORMAL yazma yolundan (`setValue` → debounce → PUT) girer — sunucudaki
// köken kaydı aynen kalır (11A: satır "Öneri elle kabul edildi"e döner, silinmez).
//
// Kabuk ConfirmDialog/TransferMenu deseninin ikizi: role=dialog + aria-modal + Esc +
// odak tuzağı + kapanışta odağın tetikleyiciye dönmesi.

import { useEffect, useId, useRef } from "react";
import { formatFormValue, type QuestionSnapshot, type ResponseProvenance } from "@mahir/shared";
import {
  IMPORT_COPY,
  canUseProposal,
  proposalFormValue,
  rejectedReasonText,
} from "../../lib/report-import-ui";

export interface RejectedProposalPopoverProps {
  open: boolean;
  entry: ResponseProvenance;
  question: QuestionSnapshot;
  onUse: () => void;
  onClose: () => void;
}

export function RejectedProposalPopover(props: RejectedProposalPopoverProps): JSX.Element | null {
  const { open, entry, question, onUse, onClose } = props;
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  // Kapanışta odak tetikleyiciye ("öneriyi gör") döner — klavye kullanıcısı
  // listenin başına fırlamaz.
  const returnFocusRef = useRef<HTMLElement | null>(null);
  // Effect deps YALNIZ [open] — `onClose` her render'da yeni referans olduğu için
  // deps'te olsaydı effect yeniden koşar, cleanup odağı "Kapat"a geri sıçratırdı.
  // Güncel closure'ı ref'te tutuyoruz.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    returnFocusRef.current = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") {
        e.preventDefault();
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab" || !dialogRef.current) return;
      const focusables = dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input, [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      returnFocusRef.current?.focus();
    };
  }, [open]);

  if (!open) return null;

  // run2 D9 — seçim tipinde saklanan şekil (`["opt_hayir"]`) form şekline çevrilir;
  // aksi halde `formatFormValue` şekli tanımaz ve ham JSON + ham seçenek kimliği
  // görünürdü. Etiket çözülemeyen kimlik `resolveChoiceLabel` kuralıyla çıplak kalır
  // (uydurma etiket yok). "Bu değeri kullan" yolu da aynı çeviriyi kullanır.
  const proposed = formatFormValue(
    proposalFormValue(entry.proposedValue, question),
    { code: question.code, type: question.type, options: question.options },
    { emptyText: "—" },
  );
  // İNCELEME (Important) — köken AYRINTISI genel sebep metnini EZER: mükerrer metin
  // reddinde ("aynı metin başka soruya yazıldı (<kod>)") "metin raporda bulunamadı"
  // demek yanlış bilgi; metin raporda birebir var, yalnız başka soruya yazılmıştı.
  const reason = rejectedReasonText(entry);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="rounded-xl p-5 max-w-lg w-full mx-4"
        style={{ background: "var(--color-surface)", border: "1px solid var(--color-border)" }}
      >
        <h2
          id={titleId}
          style={{ fontFamily: "var(--font-display)", fontSize: "1.05rem", marginBottom: 4 }}
        >
          {IMPORT_COPY.proposalTitle}
        </h2>
        {/* 15A — 14 px altı metin her yerde --color-text-2. */}
        <p style={{ fontSize: "0.78rem", color: "var(--color-text-2)", marginBottom: 12 }}>
          {question.code} · {reason}
        </p>

        <div
          style={{
            background: "var(--color-bg)",
            border: "1px solid var(--color-border)",
            borderRadius: 8,
            padding: "8px 10px",
            fontSize: "0.88rem",
            color: "var(--color-text)",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            maxHeight: 180,
            overflowY: "auto",
          }}
        >
          {proposed}
        </div>

        {entry.snippet ? (
          <div
            style={{
              marginTop: 10,
              borderLeft: "3px solid var(--color-border)",
              paddingLeft: 8,
              fontSize: "0.78rem",
              color: "var(--color-text-2)",
              fontStyle: "italic",
              whiteSpace: "pre-wrap",
            }}
          >
            {entry.snippet}
          </div>
        ) : null}

        <div className="flex justify-end gap-2" style={{ marginTop: 16 }}>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm"
            style={{
              background: "transparent",
              border: "1px solid var(--color-border)",
              color: "var(--color-text-2)",
            }}
          >
            {IMPORT_COPY.proposalClose}
          </button>
          {/* R15 — düğme YALNIZ yazılabilir öneride (grounding reddi + değer var).
              `schema` reddinde saklanan ham model çıktısı soru tipine uymaz; yazılsa
              doğrulama hatası verirdi, o yüzden öneri salt-okunur gösterilir. */}
          {canUseProposal(entry) ? (
            <button
              type="button"
              onClick={onUse}
              className="rounded-lg px-4 py-2 text-sm"
              style={{
                background: "var(--color-primary)",
                color: "var(--color-surface)",
                fontWeight: 600,
              }}
            >
              {IMPORT_COPY.useProposal}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
